/**
 * FinanzasMovimientosPage.jsx — Libro de Registro estilo Quicken del Módulo de Finanzas para CIG
 * Ruta: /finanzas/movimientos
 *
 * Características: edición inline, navegación con teclado, splits, saldo corriente
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import finanzasApi from '../../lib/finanzas_api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Trash2, Wallet, Check, X, Split, Plus, ChevronDown, ChevronRight, Download, Upload, BookOpen, DollarSign, Loader2 } from 'lucide-react';
import FinAppLayout from '../../components/FinAppLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'INGRESOS': '#dcfce7', 'GASTOS': '#fee2e2', 'INVERSIONES': '#dbeafe',
  'IMPUESTOS': '#ffedd5', 'REMUNERACIONES': '#ede9fe',
};
const currencySymbols = { CLP: '$', USD: 'US$', ARS: 'AR$' };

const fmtMoney = (amt) => {
  if (!amt || amt === 0) return '';
  return new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amt);
};

const todayStr = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
};

const COLS = ['fecha', 'descripcion', 'ingreso', 'egreso', 'cuenta_contraparte_id', 'monto_equivalente', 'categoria_id'];

const emptyRow = () => ({
  fecha: todayStr(), descripcion: '', ingreso: '', egreso: '',
  cuenta_contraparte_id: '', monto_equivalente: '', categoria_id: '', splits: [],
});

const emptySplit = () => ({ id: '', descripcion: '', monto: '', categoria_id: '' });

// Convierte movimiento del backend (tipo+monto) a representación ingreso/egreso
const movToDisplay = (m) => ({
  ...m,
  ingreso: m.tipo === 'Ingreso' ? m.monto : 0,
  egreso:  m.tipo === 'Gasto'   ? m.monto : 0,
  monto_equivalente: m.monto_equivalente || null,
  moneda_equivalente: m.moneda_equivalente || null,
});

export default function FinanzasMovimientosPage() {
  const [movimientos, setMovimientos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCuenta, setFilterCuenta] = useState('all');

  const [editingRowId, setEditingRowId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [newRow, setNewRow] = useState(emptyRow());
  const [savingRow, setSavingRow] = useState(null);

  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showNewRowSplits, setShowNewRowSplits] = useState(false);

  // Estado para dólar observado
  const [tcFecha, setTcFecha] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [tcValor, setTcValor] = useState(null);
  const [tcLoading, setTcLoading] = useState(false);

  const cellRefs = useRef({});
  const newRowRefs = useRef({});

  const fetchData = useCallback(async () => {
    try {
      const [mRes, cRes, catRes] = await Promise.all([
        finanzasApi.get('/movimientos'), finanzasApi.get('/cuentas'), finanzasApi.get('/categorias'),
      ]);
      setMovimientos(mRes.data.map(movToDisplay));
      setCuentas(cRes.data);
      setCategorias(catRes.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Obtener TC del Banco Central cuando cambia la fecha
  const fetchTC = useCallback(async (fecha) => {
    if (!fecha) return;
    setTcLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/cuentas-por-cobrar/tc-cmf/${fecha}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTcValor(res.data.tipo_cambio);
    } catch (err) {
      setTcValor(null);
      toast.error('No se pudo obtener el TC');
    } finally {
      setTcLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tcFecha) fetchTC(tcFecha);
  }, [tcFecha, fetchTC]);

  const getCuenta = (id) => cuentas.find(c => c.id === id);
  const getCuentaName = (id) => getCuenta(id)?.nombre || '';
  const getCuentaCurrency = (id) => getCuenta(id)?.moneda_base || 'CLP';
  const getCategoriaName = (id) => categorias.find(c => c.id === id)?.nombre || '';

  const filteredCuentaObj = filterCuenta !== 'all' ? getCuenta(filterCuenta) : null;
  const activeSym = currencySymbols[filteredCuentaObj?.moneda_base || 'CLP'] || '$';

  const sorted = useMemo(() => {
    if (filterCuenta === 'all') return [];
    return [...movimientos]
      .filter(m => m.cuenta_id === filterCuenta)
      .sort((a, b) => {
        const parseDate = (f) => { const [d,m,y] = (f||'').split('-').map(Number); return new Date(y||0, (m||1)-1, d||1); };
        return parseDate(a.fecha) - parseDate(b.fecha);
      });
  }, [movimientos, filterCuenta]);

  const rowsWithBalance = useMemo(() => {
    let balance = filteredCuentaObj?.saldo_inicial || 0;
    return sorted.map(m => { balance += (m.ingreso || 0) - (m.egreso || 0); return { ...m, _balance: balance }; });
  }, [sorted, filteredCuentaObj]);

  const totals = useMemo(() => {
    let ing = 0, egr = 0;
    sorted.forEach(m => { ing += m.ingreso || 0; egr += m.egreso || 0; });
    return { ingresos: ing, egresos: egr, balance: ing - egr };
  }, [sorted]);

  const focusCell = (rowId, colIdx) => {
    setTimeout(() => {
      const col = COLS[colIdx];
      const el = rowId === 'new' ? newRowRefs.current[col] : cellRefs.current[`${rowId}-${col}`];
      if (el) el.focus();
    }, 30);
  };

  const handleKeyDown = (e, rowId, colIdx) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (colIdx < COLS.length - 1) focusCell(rowId, colIdx + 1);
      else { rowId === 'new' ? saveNewRow() : saveEditRow(rowId); }
    } else if (e.key === 'Tab' && !e.shiftKey && colIdx === COLS.length - 1) {
      e.preventDefault();
      rowId === 'new' ? saveNewRow() : saveEditRow(rowId);
    } else if (e.key === 'Escape') {
      rowId === 'new' ? setNewRow(emptyRow()) : cancelEdit();
    }
  };

  const startEdit = (mov) => {
    if (editingRowId === mov.id) return;
    setEditingRowId(mov.id);
    setEditData({
      fecha: mov.fecha, descripcion: mov.descripcion,
      ingreso: mov.ingreso > 0 ? String(mov.ingreso) : '',
      egreso:  mov.egreso  > 0 ? String(mov.egreso)  : '',
      cuenta_contraparte_id: mov.cuenta_contraparte_id || '',
      monto_equivalente: mov.monto_equivalente ? String(mov.monto_equivalente) : '',
      categoria_id: mov.categoria_id,
      splits: (mov.splits || []).map(s => ({ ...s, monto: String(s.monto) })),
    });
    if (mov.splits?.length > 0) setExpandedRows(prev => new Set([...prev, mov.id]));
    focusCell(mov.id, 0);
  };

  const cancelEdit = () => { setEditingRowId(null); setEditData(null); };

  const saveEditRow = async (rowId) => {
    if (!editData || !editData.descripcion.trim()) return;
    const ingreso = parseFloat(editData.ingreso) || 0;
    const egreso  = parseFloat(editData.egreso)  || 0;
    if (ingreso === 0 && egreso === 0) { toast.error('Ingresa un monto'); return; }
    const tipo  = ingreso > 0 ? 'Ingreso' : 'Gasto';
    const monto = ingreso > 0 ? ingreso : egreso;
    const splits = editData.splits.filter(s => s.categoria_id && parseFloat(s.monto) > 0).map(s => ({
      id: s.id || undefined, descripcion: s.descripcion || '', monto: parseFloat(s.monto), categoria_id: s.categoria_id,
    }));
    
    // Monto equivalente para doble moneda
    const montoEquiv = editData.monto_equivalente ? parseFloat(editData.monto_equivalente) : null;
    const cuentaContra = editData.cuenta_contraparte_id ? getCuenta(editData.cuenta_contraparte_id) : null;
    const monedaEquiv = cuentaContra?.moneda_base || cuentaContra?.moneda || null;
    
    setSavingRow(rowId);
    try {
      await finanzasApi.put(`/movimientos/${rowId}`, {
        fecha: editData.fecha, descripcion: editData.descripcion,
        tipo, monto, categoria_id: editData.categoria_id,
        monto_equivalente: montoEquiv,
        moneda_equivalente: monedaEquiv,
        splits: splits.length > 0 ? splits : [],
      });
      toast.success('Movimiento actualizado'); cancelEdit(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al actualizar'); }
    finally { setSavingRow(null); }
  };

  const saveNewRow = async () => {
    if (filterCuenta === 'all') { toast.error('Selecciona una cuenta primero'); return; }
    if (!newRow.descripcion.trim()) { focusCell('new', 1); return; }
    if (!newRow.categoria_id) { toast.error('Selecciona una categoría'); return; }
    const ingreso = parseFloat(newRow.ingreso) || 0;
    const egreso  = parseFloat(newRow.egreso)  || 0;
    if (ingreso === 0 && egreso === 0) { toast.error('Ingresa un monto'); return; }
    const tipo  = ingreso > 0 ? 'Ingreso' : 'Gasto';
    const monto = ingreso > 0 ? ingreso : egreso;
    const splits = (newRow.splits || []).filter(s => s.categoria_id && parseFloat(s.monto) > 0).map(s => ({
      descripcion: s.descripcion || '', monto: parseFloat(s.monto), categoria_id: s.categoria_id,
    }));
    
    // Monto equivalente para doble moneda
    const montoEquiv = newRow.monto_equivalente ? parseFloat(newRow.monto_equivalente) : null;
    const cuentaContra = newRow.cuenta_contraparte_id ? getCuenta(newRow.cuenta_contraparte_id) : null;
    const monedaEquiv = cuentaContra?.moneda_base || cuentaContra?.moneda || null;
    
    setSavingRow('new');
    try {
      await finanzasApi.post('/movimientos', {
        fecha: newRow.fecha, descripcion: newRow.descripcion,
        tipo, monto,
        cuenta_id: filterCuenta,
        categoria_id: newRow.categoria_id,
        cuenta_contraparte_id: newRow.cuenta_contraparte_id || null,
        monto_equivalente: montoEquiv,
        moneda_equivalente: monedaEquiv,
        splits: splits.length > 0 ? splits : null,
      });
      toast.success('Movimiento creado');
      setNewRow(emptyRow()); setShowNewRowSplits(false); fetchData();
      setTimeout(() => focusCell('new', 0), 150);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear'); }
    finally { setSavingRow(null); }
  };

  const handleDelete = async (id) => {
    try {
      await finanzasApi.delete(`/movimientos/${id}`);
      toast.success('Movimiento eliminado'); if (editingRowId === id) cancelEdit(); fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const toggleExpand = (movId) => setExpandedRows(prev => { const next = new Set(prev); next.has(movId) ? next.delete(movId) : next.add(movId); return next; });

  const addSplit = (isNew) => {
    if (isNew) { setNewRow(r => ({ ...r, splits: [...(r.splits || []), emptySplit()] })); if (!showNewRowSplits) setShowNewRowSplits(true); }
    else if (editData) { setEditData(d => ({ ...d, splits: [...(d.splits || []), emptySplit()] })); }
  };
  const updateSplit = (isNew, idx, field, value) => {
    if (isNew) { setNewRow(r => { const s = [...(r.splits||[])]; s[idx]={...s[idx],[field]:value}; return {...r,splits:s}; }); }
    else if (editData) { setEditData(d => { const s=[...(d.splits||[])]; s[idx]={...s[idx],[field]:value}; return {...d,splits:s}; }); }
  };
  const removeSplit = (isNew, idx) => {
    if (isNew) { setNewRow(r => ({ ...r, splits: (r.splits||[]).filter((_,i)=>i!==idx) })); }
    else if (editData) { setEditData(d => ({ ...d, splits: (d.splits||[]).filter((_,i)=>i!==idx) })); }
  };
  const getSplitsTotal = (splits) => (splits||[]).reduce((s,x) => s+(parseFloat(x.monto)||0), 0);

  const handleExport = async () => {
    try {
      const params = filterCuenta !== 'all' ? `?cuenta_id=${filterCuenta}` : '';
      const response = await finanzasApi.get(`/movimientos/export${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `movimientos_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado');
    } catch { toast.error('Error al exportar'); }
  };

  const importRef = useRef(null);
  const handleImport = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await finanzasApi.post('/movimientos/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${res.data.imported} movimientos importados`);
      if (res.data.errors?.length > 0) toast.error(`${res.data.errors.length} filas con errores`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al importar'); }
    finally { if (importRef.current) importRef.current.value = ''; }
  };

  useEffect(() => { setNewRow(emptyRow()); }, [filterCuenta]);

  const inputCls = "w-full h-full bg-transparent border-0 outline-none text-[13px] px-1.5 py-0 focus:ring-0 tabular-nums";

  const renderDisplayCell = (mov, col) => {
    const hasSplits = mov.splits?.length > 0;
    switch (col) {
      case 'fecha': return <span className="tabular-nums text-slate-600 text-[13px]">{mov.fecha}</span>;
      case 'descripcion': return <span className="text-slate-800 text-[13px] truncate block max-w-[260px]">{mov.descripcion}</span>;
      case 'ingreso': return mov.ingreso > 0 ? <span className="tabular-nums text-emerald-700 font-medium text-[13px]">{fmtMoney(mov.ingreso)}</span> : <span className="text-slate-300 text-[13px]">—</span>;
      case 'egreso':  return mov.egreso  > 0 ? <span className="tabular-nums text-rose-600 font-medium text-[13px]">{fmtMoney(mov.egreso)}</span>  : <span className="text-slate-300 text-[13px]">—</span>;
      case 'cuenta_id': return <span className="text-[13px] text-slate-600">{getCuentaName(mov.cuenta_id)}</span>;
      case 'cuenta_contraparte_id': {
        if (!mov.cuenta_contraparte_id) return <span className="text-slate-300 text-[13px]">—</span>;
        return <span className="text-[13px] text-slate-600">{getCuentaName(mov.cuenta_contraparte_id)}</span>;
      }
      case 'monto_equivalente': {
        if (!mov.monto_equivalente) return <span className="text-slate-300 text-[13px]">—</span>;
        const monedaSym = currencySymbols[mov.moneda_equivalente] || '';
        return <span className="tabular-nums text-indigo-600 font-medium text-[13px]">{monedaSym} {fmtMoney(mov.monto_equivalente)}</span>;
      }
      case 'categoria_id': {
        if (hasSplits) return <span className="text-[12px] font-medium px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600">Desglose</span>;
        const name = getCategoriaName(mov.categoria_id);
        return <span className="text-[12px] font-medium px-2 py-0.5 rounded-md" style={{ backgroundColor: CATEGORY_COLORS[name] || '#f1f5f9' }}>{name}</span>;
      }
      default: return null;
    }
  };

  const renderEditCell = (data, setData, rowId, colIdx) => {
    const col = COLS[colIdx];
    const setRef = (el) => { if (rowId === 'new') newRowRefs.current[col] = el; else cellRefs.current[`${rowId}-${col}`] = el; };
    const onKey = (e) => handleKeyDown(e, rowId, colIdx);
    
    // Determinar si las monedas son diferentes para mostrar campo monto_equivalente
    const cuentaActual = getCuenta(filterCuenta);
    const cuentaContra = data.cuenta_contraparte_id ? getCuenta(data.cuenta_contraparte_id) : null;
    const monedaActual = cuentaActual?.moneda_base || cuentaActual?.moneda || 'CLP';
    const monedaContra = cuentaContra?.moneda_base || cuentaContra?.moneda || null;
    const monedasDiferentes = cuentaContra && monedaContra && monedaActual !== monedaContra;
    
    switch (col) {
      case 'fecha': return <input ref={setRef} type="text" value={data.fecha} onChange={e => setData({...data,fecha:e.target.value})} onKeyDown={onKey} className={`${inputCls} text-slate-700 w-[100px]`} placeholder="dd-mm-aaaa" data-testid={`cell-fecha-${rowId}`}/>;
      case 'descripcion': return <input ref={setRef} type="text" value={data.descripcion} onChange={e => setData({...data,descripcion:e.target.value})} onKeyDown={onKey} className={`${inputCls} text-slate-800`} placeholder="Descripción..." data-testid={`cell-desc-${rowId}`}/>;
      case 'ingreso': return <input ref={setRef} type="number" step="0.01" min="0" value={data.ingreso} onChange={e => setData({...data,ingreso:e.target.value,egreso:''})} onKeyDown={onKey} className={`${inputCls} text-emerald-700 text-right`} placeholder="0" data-testid={`cell-ing-${rowId}`}/>;
      case 'egreso':  return <input ref={setRef} type="number" step="0.01" min="0" value={data.egreso}  onChange={e => setData({...data,egreso:e.target.value,ingreso:''})} onKeyDown={onKey} className={`${inputCls} text-rose-600 text-right`}    placeholder="0" data-testid={`cell-egr-${rowId}`}/>;
      case 'cuenta_id': return (
        <select ref={setRef} value={data.cuenta_id} onChange={e => setData({...data,cuenta_id:e.target.value})} onKeyDown={onKey} className={`${inputCls} cursor-pointer`} data-testid={`cell-cta-${rowId}`}>
          <option value="">— Cuenta —</option>
          {cuentas.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda_base})</option>)}
        </select>
      );
      case 'cuenta_contraparte_id': return (
        <select ref={setRef} value={data.cuenta_contraparte_id || ''} onChange={e => setData({...data,cuenta_contraparte_id:e.target.value,monto_equivalente:''})} onKeyDown={onKey} className={`${inputCls} cursor-pointer`} data-testid={`cell-contra-${rowId}`}>
          <option value="">— Cuenta —</option>
          {cuentas.filter(c => c.id !== filterCuenta).map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda_base || c.moneda})</option>)}
        </select>
      );
      case 'monto_equivalente': {
        if (!monedasDiferentes) {
          return <span className="text-slate-300 text-[13px]">—</span>;
        }
        const monedaSym = currencySymbols[monedaContra] || monedaContra;
        return (
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-indigo-500 font-medium">{monedaSym}</span>
            <input 
              ref={setRef} 
              type="number" 
              step="0.01" 
              min="0" 
              value={data.monto_equivalente || ''} 
              onChange={e => setData({...data, monto_equivalente: e.target.value})} 
              onKeyDown={onKey} 
              className={`${inputCls} text-indigo-600 text-right w-[80px]`} 
              placeholder="0" 
              data-testid={`cell-equiv-${rowId}`}
            />
          </div>
        );
      }
      case 'categoria_id': return (
        <select ref={setRef} value={data.categoria_id} onChange={e => setData({...data,categoria_id:e.target.value})} onKeyDown={onKey} className={`${inputCls} cursor-pointer`} data-testid={`cell-cat-${rowId}`}>
          <option value="">— Categoría —</option>
          {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
      );
      default: return null;
    }
  };

  function renderSplitRows(splits, isEditing, isNew, movTotal) {
    if (!splits || splits.length === 0) return null;
    const splitsTotal = getSplitsTotal(splits);
    const remaining = movTotal - splitsTotal;
    return (
      <React.Fragment>
        {splits.map((sp, idx) => (
          <tr key={sp.id || `split-${idx}`} className="bg-indigo-50/20 border-b border-slate-100/60" data-testid={`split-row-${idx}`}>
            <td className="pl-8 pr-2 py-1 h-8" colSpan={2}>
              <div className="flex items-center gap-2">
                <span className="text-indigo-300 text-[11px]">└</span>
                {isEditing ? (
                  <input type="text" value={sp.descripcion} onChange={e => updateSplit(isNew, idx, 'descripcion', e.target.value)} className="flex-1 bg-transparent border-0 outline-none text-[12px] text-slate-700 px-1" placeholder="Detalle..." data-testid={`split-desc-${idx}`}/>
                ) : <span className="text-[12px] text-slate-600">{sp.descripcion || '—'}</span>}
              </div>
            </td>
            <td className="px-2 py-1 h-8 text-right" colSpan={2}>
              {isEditing ? (
                <input type="number" step="0.01" min="0" value={sp.monto} onChange={e => updateSplit(isNew, idx, 'monto', e.target.value)} className="w-[100px] bg-transparent border-0 outline-none text-[12px] text-slate-700 text-right tabular-nums px-1" placeholder="0" data-testid={`split-monto-${idx}`}/>
              ) : <span className="tabular-nums text-[12px] text-slate-700 font-medium">{fmtMoney(parseFloat(sp.monto)||0)}</span>}
            </td>
            <td className="px-2 py-1 h-8">
              {isEditing ? (
                <select value={sp.categoria_id} onChange={e => updateSplit(isNew, idx, 'categoria_id', e.target.value)} className="w-full bg-transparent border-0 outline-none text-[12px] cursor-pointer px-1" data-testid={`split-cat-${idx}`}>
                  <option value="">— Cat. —</option>
                  {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              ) : (
                <span className="text-[12px] font-medium px-2 py-0.5 rounded-md" style={{ backgroundColor: CATEGORY_COLORS[getCategoriaName(sp.categoria_id)] || '#f1f5f9' }}>
                  {getCategoriaName(sp.categoria_id)}
                </span>
              )}
            </td>
            <td className="px-1 py-1 h-8">
              {isEditing && <button onClick={() => removeSplit(isNew, idx)} className="p-0.5 rounded hover:bg-rose-50 text-slate-300 hover:text-rose-500" data-testid={`split-remove-${idx}`}><X className="w-3 h-3"/></button>}
            </td>
          </tr>
        ))}
        <tr className="bg-indigo-50/30 border-b border-indigo-200/40">
          <td className="pl-8 pr-2 py-1 h-7" colSpan={2}>
            {isEditing && <button onClick={() => addSplit(isNew)} className="flex items-center gap-1 text-[11px] text-indigo-500 hover:text-indigo-700 font-medium" data-testid={`add-split-btn-${isNew?'new':'edit'}`}><Plus className="w-3 h-3"/> Agregar línea</button>}
          </td>
          <td className="px-2 py-1 h-7 text-right" colSpan={2}>
            <span className={`text-[11px] font-semibold tabular-nums ${Math.abs(remaining) < 0.01 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {Math.abs(remaining) < 0.01 ? 'Cuadrado' : `Faltan: ${fmtMoney(remaining)}`}
            </span>
          </td>
          <td colSpan={2}></td>
        </tr>
      </React.Fragment>
    );
  }

  return (
    <FinAppLayout>
      <div data-testid="finanzas-movimientos-page" className="flex flex-col">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-800 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {filterCuenta !== 'all' && filteredCuentaObj
                ? <>{filteredCuentaObj.nombre} <span className="text-slate-400 font-semibold text-2xl">· {filteredCuentaObj.moneda_base}</span></>
                : 'Libro de Movimientos'}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {filterCuenta !== 'all' ? 'Edita directamente en la tabla. Enter para avanzar.' : 'Selecciona una cuenta para ver su libro de movimientos.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={importRef} type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" data-testid="import-file-input" />
            <Button variant="outline" onClick={() => importRef.current?.click()} className="h-9 rounded-lg text-sm gap-1.5 border-slate-200 text-slate-600" data-testid="import-btn"><Upload className="w-3.5 h-3.5" /> Importar</Button>
            <Button variant="outline" onClick={handleExport} className="h-9 rounded-lg text-sm gap-1.5 border-slate-200 text-slate-600" data-testid="export-btn"><Download className="w-3.5 h-3.5" /> Exportar</Button>
            <Select value={filterCuenta} onValueChange={setFilterCuenta}>
              <SelectTrigger className="w-[230px] h-9 bg-white border-slate-200 rounded-lg text-sm" data-testid="filter-cuenta-select"><SelectValue placeholder="Seleccionar cuenta" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">— Seleccionar cuenta —</SelectItem>
                {cuentas.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre} ({currencySymbols[c.moneda_base]} {c.moneda_base})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-6 mb-4 px-1 flex-wrap" data-testid="movimientos-summary">
          <div className="flex items-center gap-2"><span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Ingresos</span><span className="text-sm font-bold text-emerald-600 tabular-nums">{activeSym} {fmtMoney(totals.ingresos)}</span></div>
          <div className="w-px h-4 bg-slate-200"/>
          <div className="flex items-center gap-2"><span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Egresos</span><span className="text-sm font-bold text-rose-600 tabular-nums">{activeSym} {fmtMoney(totals.egresos)}</span></div>
          <div className="w-px h-4 bg-slate-200"/>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Balance</span>
            <span className={`text-sm font-bold tabular-nums ${totals.balance >= 0 ? 'text-indigo-600' : 'text-rose-600'}`}>{activeSym} {fmtMoney(totals.balance)}</span>
          </div>
          
          {/* Selector de fecha para dólar observado */}
          <div className="ml-auto flex items-center gap-0 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg overflow-hidden">
            <input
              type="date"
              value={tcFecha}
              onChange={(e) => setTcFecha(e.target.value)}
              className="h-full text-sm font-mono bg-white border-0 px-3 py-2 text-slate-700 focus:ring-0 focus:outline-none"
              data-testid="tc-fecha-selector"
            />
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-100/50 border-l border-blue-200">
              <div className="flex items-center gap-1">
                <DollarSign size={16} className="text-blue-600" />
                <span className="text-sm font-bold text-blue-600">USD OBS.</span>
              </div>
              {tcLoading ? (
                <Loader2 size={16} className="text-blue-500 animate-spin" />
              ) : tcValor ? (
                <span className="text-lg font-bold text-blue-700 tabular-nums" data-testid="tc-valor">
                  ${tcValor.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              ) : (
                <span className="text-sm text-slate-400">—</span>
              )}
            </div>
          </div>
          
          <div className="text-xs text-slate-400">{sorted.length} mov.</div>
        </div>

        {cuentas.length === 0 && !loading ? (
          <div className="text-center py-20" data-testid="no-cuentas-hint">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-50 flex items-center justify-center mb-4"><Wallet className="w-8 h-8 text-amber-400" strokeWidth={1.5}/></div>
            <h3 className="text-lg font-semibold text-slate-700">Primero crea una cuenta</h3>
            <p className="mt-2 text-sm text-slate-500">Necesitas al menos una cuenta para registrar movimientos</p>
          </div>
        ) : filterCuenta === 'all' ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <BookOpen className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-slate-600 font-semibold text-lg">Selecciona una cuenta</p>
            <p className="text-slate-400 text-sm mt-1 max-w-xs">Elige una cuenta en el selector de arriba para ver su libro de movimientos.</p>
          </div>
        ) : loading ? (
          <div className="space-y-2">{[...Array(8)].map((_,i) => <div key={i} className="h-9 bg-slate-100/50 rounded animate-pulse"/>)}</div>
        ) : (
          <Card className="border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="register-table-card">
            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse" data-testid="register-table">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-100 border-b-2 border-slate-300">
                    <th className="px-2 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[110px]">Fecha</th>
                    <th className="px-2 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider min-w-[160px]">Descripción</th>
                    <th className="px-2 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[90px]">Ingreso</th>
                    <th className="px-2 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[90px]">Egreso</th>
                    <th className="px-2 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[140px]">Contra Cuenta</th>
                    <th className="px-2 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[100px]">Equivalente</th>
                    <th className="px-2 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[120px]">Categoría</th>
                    <th className="px-2 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[100px]">Saldo</th>
                    <th className="px-1 py-2.5 w-[60px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {rowsWithBalance.map((mov, idx) => {
                    const isEditing = editingRowId === mov.id;
                    const isSaving  = savingRow === mov.id;
                    const hasSplits = (mov.splits?.length > 0) || (isEditing && editData?.splits?.length > 0);
                    const isExpanded = expandedRows.has(mov.id);
                    const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40';
                    return (
                      <React.Fragment key={mov.id}>
                        <tr className={`border-b border-slate-100 transition-colors ${isEditing ? 'bg-indigo-50/60 ring-1 ring-inset ring-indigo-200' : `${rowBg} hover:bg-indigo-50/30`} ${isSaving ? 'opacity-60' : ''} cursor-pointer`}
                          onClick={() => !isEditing && startEdit(mov)} data-testid={`register-row-${mov.id}`}>
                          {COLS.map((col, colIdx) => (
                            <td key={col} className={`px-0 py-0 h-9 ${col==='ingreso'||col==='egreso' ? 'text-right' : ''}`}>
                              <div className="px-2 py-1 h-full flex items-center">
                                {col === 'fecha' && hasSplits && (
                                  <button onClick={(e) => { e.stopPropagation(); toggleExpand(mov.id); }} className="mr-1 p-0.5 rounded hover:bg-indigo-100 text-indigo-400" data-testid={`toggle-splits-${mov.id}`}>
                                    {isExpanded ? <ChevronDown className="w-3 h-3"/> : <ChevronRight className="w-3 h-3"/>}
                                  </button>
                                )}
                                {isEditing ? renderEditCell(editData, setEditData, mov.id, colIdx) : renderDisplayCell(mov, col)}
                              </div>
                            </td>
                          ))}
                          <td className="px-2 py-1 h-9 text-right">
                            <span className={`tabular-nums text-[13px] font-medium ${mov._balance >= 0 ? 'text-slate-700' : 'text-rose-600'}`}>{fmtMoney(mov._balance)}</span>
                          </td>
                          <td className="px-1 py-1 h-9">
                            <div className="flex items-center gap-0.5">
                              {isEditing ? (
                                <>
                                  <button onClick={(e) => { e.stopPropagation(); addSplit(false); if (!isExpanded) toggleExpand(mov.id); }} className="p-1 rounded hover:bg-indigo-100 text-indigo-400 hover:text-indigo-600" title="Split" data-testid={`split-btn-${mov.id}`}><Split className="w-3.5 h-3.5"/></button>
                                  <button onClick={(e) => { e.stopPropagation(); saveEditRow(mov.id); }} className="p-1 rounded hover:bg-emerald-100 text-emerald-600" title="Guardar" data-testid={`save-row-${mov.id}`}><Check className="w-3.5 h-3.5"/></button>
                                  <button onClick={(e) => { e.stopPropagation(); cancelEdit(); }} className="p-1 rounded hover:bg-slate-200 text-slate-400" title="Cancelar" data-testid={`cancel-row-${mov.id}`}><X className="w-3.5 h-3.5"/></button>
                                </>
                              ) : (
                                <button onClick={(e) => { e.stopPropagation(); handleDelete(mov.id); }} className="p-1 rounded hover:bg-rose-50 text-slate-300 hover:text-rose-500" title={mov.es_contraparte ? 'Eliminar partida completa' : 'Eliminar'} data-testid={`delete-row-${mov.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && renderSplitRows(
                          isEditing ? editData.splits : (mov.splits || []),
                          isEditing, false,
                          isEditing ? (parseFloat(editData.ingreso)||0)+(parseFloat(editData.egreso)||0) : (mov.ingreso||0)+(mov.egreso||0)
                        )}
                      </React.Fragment>
                    );
                  })}
                  {/* Fila de nueva entrada */}
                  <tr className={`border-b-2 border-indigo-200 bg-indigo-50/30 ${savingRow==='new'?'opacity-60':''}`} data-testid="new-entry-row">
                    {COLS.map((col, colIdx) => (
                      <td key={col} className={`px-0 py-0 h-10 ${col==='ingreso'||col==='egreso'?'text-right':''}`}>
                        <div className="px-2 py-1 h-full flex items-center">{renderEditCell(newRow, setNewRow, 'new', colIdx)}</div>
                      </td>
                    ))}
                    <td className="px-2 py-1 h-10 text-right"><span className="text-slate-300 text-[13px]">—</span></td>
                    <td className="px-1 py-1 h-10">
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => addSplit(true)} className="p-1 rounded hover:bg-indigo-100 text-indigo-400 hover:text-indigo-600" title="Split" data-testid="split-btn-new"><Split className="w-3.5 h-3.5"/></button>
                        <button onClick={saveNewRow} className="p-1 rounded hover:bg-indigo-100 text-indigo-400 hover:text-indigo-600" title="Guardar" data-testid="save-new-row-btn"><Check className="w-3.5 h-3.5"/></button>
                      </div>
                    </td>
                  </tr>
                  {showNewRowSplits && newRow.splits?.length > 0 && renderSplitRows(newRow.splits, true, true, (parseFloat(newRow.ingreso)||0)+(parseFloat(newRow.egreso)||0))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <p className="text-[11px] text-slate-400">
                <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono">Enter</kbd> siguiente celda
                <span className="mx-2">·</span>
                <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono">Esc</kbd> cancelar
              </p>
              <p className="text-[11px] text-slate-400">Clic en fila para editar</p>
            </div>
          </Card>
        )}
      </div>
    </FinAppLayout>
  );
}
